'use client';

import { useEffect } from 'react';

/**
 * @name FocusVisibleOutline
 * @description Creates focus outline for interactive elements
 * @returns {JSX.Element} ReactElement
 */
export default function FocusVisibleOutline() {
  useEffect(() => {
    /**
     * @name handleFocus
     * @description Adds foucs to interactive elements
     * @param {object} e - event
     */
    const handleFocus = e => {
      const el = e.target;

      // Only apply focus styles on keyboard focus (tab navigation)
      if (!(el.tabIndex >= 0 && el.matches(':focus-visible'))) return;

      // Skip radiogroup div — it has tabindex="0" but we don't want outer focus ring
      if (el.getAttribute('role') === 'radiogroup') return;

      /**
       * @name getClosestBackgroundColor
       * @description Gets the background color of element
       * @param {HTMLElement} element -  element
       * @returns {{ color: string, isSolid: boolean }} - background color and whether it is solid
       */
      const getClosestBackgroundColor = element => {
        let current = element.parentElement;
        let solidColor = null;
        while (current && current !== document) {
          // Check background image FIRST before solid color
          const bgImage = window.getComputedStyle(current).backgroundImage;
          if (bgImage && bgImage !== 'none') {
            return { color: 'transparent', isSolid: false };
          }
          // Store first solid color found but keep walking up to check for bg image
          const bgColor = window.getComputedStyle(current).backgroundColor;
          if (bgColor && !bgColor.includes('rgba(0, 0, 0, 0)') && bgColor !== 'transparent') {
            if (!solidColor) solidColor = bgColor;
          }
          current = current.parentElement;
        }
        return solidColor
          ? { color: solidColor, isSolid: true }
          : { color: '#000000', isSolid: true }; // fallback to black
      };

      const { color: bg, isSolid } = getClosestBackgroundColor(el);

      // Fix for inline elements like <a> wrapping <img> — box-shadow needs block/inline-block
      if (window.getComputedStyle(el).display === 'inline') {
        el.style.display = 'inline-block';
      }

      // Remove default browser outline — we apply our own via box-shadow
      el.style.outline = 'none';

      if (!isSolid) {
        // Element sits on a background image — use element's own bg color as gap layer
        // so the focus ring gap blends with the element rather than showing a hard edge
        el.style.borderColor = 'transparent';
        el.style.boxShadow = `
      0 0 0 3px #493553,
      0 0 0 5px #FFFFFF,
      0 0 0 7px #493553
    `;
      } else {
        // Element sits on a solid background — use bg color as gap layer
        // so the focus ring gap matches the background seamlessly
        el.style.boxShadow = `
      0 0 0 3px ${bg},
      0 0 0 5px #FFFFFF,
      0 0 0 7px #493553
    `;
      }

      // Apply border radius to match focus ring to element shape
      el.style.borderRadius = '3px';
    };

    /**
     * @name handleBlur
     * @description Removes all custom focus styles when element loses focus
     * @param {FocusEvent} e - The blur event
     */
    const handleBlur = e => {
      const el = e.target;

      // Skip radiogroup div — no styles were applied so nothing to clear
      if (el.getAttribute('role') === 'radiogroup') return;

      // Clear all custom focus styles
      el.style.outline = '';
      el.style.outlineOffset = '';
      el.style.boxShadow = '';
      el.style.borderRadius = '';
      el.style.borderColor = '';
    };
    // const handleFocus = e => {
    //   const el = e.target;

    //   // only on keyboard focus
    //   if (!(el.tabIndex >= 0 && el.matches(':focus-visible'))) return;

    //   /**
    //    * @name getClosestBackgroundColor
    //    * @description Gets the background color of element
    //    * @param {object} element - element
    //    * @returns {JSX.Element} background color
    //    */
    //   const getClosestBackgroundColor = element => {
    //     let current = element.parentElement;
    //     while (current && current !== document) {
    //       const bgColor = window.getComputedStyle(current).backgroundColor;
    //       if (bgColor && !bgColor.includes('rgba(0, 0, 0, 0)') && bgColor !== 'transparent') {
    //         return bgColor;
    //       }
    //       current = current.parentElement;
    //     }
    //     return '#000000'; // fallback
    //   };

    //   const bg = getClosestBackgroundColor(el);

    //   /**
    //    * @name isLight
    //    * @description Checks the background color is light or not
    //    * @param {object} hex - color object
    //    * @returns {JSX.Element} true/false
    //    */
    //   const isLight = hex => {
    //     const r = parseInt(hex.slice(1, 3), 16);
    //     const g = parseInt(hex.slice(3, 5), 16);
    //     const b = parseInt(hex.slice(5, 7), 16);

    //     const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

    //     return luminance > 180;
    //   };

    //   /**
    //    * @name toHex
    //    * @description converts RGB to HEX
    //    * @param {object} rgb - rgb object
    //    * @returns {JSX.Element} hex code
    //    */
    //   const toHex = rgb => {
    //     const [r, g, b] = rgb.match(/\d+/g).map(Number);
    //     return `#${[r, g, b].map(n => n.toString(16).padStart(2, '0')).join('')}`;
    //   };

    //   const hex = toHex(bg);
    //   const outlineColor = isLight(hex) ? '#DD1D78' : '#F5EBDA';
    //   el.style.outline = `3px solid ${outlineColor}`;
    //   el.style.outlineOffset = '3px';
    //   el.style.borderRadius = '3px';
    // };

    // /**
    //  * @name handleBlur
    //  * @description removes the outline if no interactive element
    //  * @param {object} e - event
    //  */
    // const handleBlur = e => {
    //   const el = e.target;

    //   if (el.tabIndex >= 0) {
    //     el.style.outline = '';
    //     el.style.outlineOffset = '';
    //   }
    // };

    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);

    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, []);

  return null; // no UI, just behavior
}
